import {
    SEARCH_MODIFIER_POPPER_Z_INDEX,
    useSearchContainerPopperAnchor
} from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/useSearchContainerPopperAnchor'
import { Box, Button, IconButton, MenuItem, Paper, Popper, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useRef } from 'react'

interface ITravelersInput {
    adults: number
    childs: number
    infants: number
    flightClass: string
    onTravelerChange: (type: 'adults' | 'childs' | 'infants', delta: number) => void
    onFlightClassChange: (newClass: string) => void
    canIncreaseAdults: boolean
    canIncreaseChildren: boolean
    canIncreaseInfants: boolean
    canDecreaseAdults: boolean
    canDecreaseChildren: boolean
    canDecreaseInfants: boolean
    onChange?: (data: { adults: number; childs: number; infants: number; flightClass: string }) => void
    active: boolean
    onActivate: () => void
    onClose: () => void
    /** Anchor dropdown flush to the search bar bottom instead of the trigger. */
    attachToSearchContainer?: boolean
}

export const TravelersInput: React.FC<ITravelersInput> = props => {
    const travelersRef = useRef<HTMLDivElement>(null)
    const t = useTranslations('common')

    const TRAVELER_TYPES = [
        { key: 'adults', label: t('adult'), min: 1 },
        { key: 'childs', label: t('child'), min: 0 },
        { key: 'infants', label: t('infant'), min: 0 }
    ]

    const {
        adults,
        childs,
        infants,
        flightClass,
        onTravelerChange,
        onFlightClassChange,
        canIncreaseAdults,
        canIncreaseChildren,
        canIncreaseInfants,
        canDecreaseAdults,
        canDecreaseChildren,
        canDecreaseInfants,
        onChange,
        active,
        onActivate,
        onClose,
        attachToSearchContainer = false
    } = props

    const { anchorEl, offset, popperOpen } = useSearchContainerPopperAnchor(
        active,
        travelersRef,
        attachToSearchContainer
    )

    const totalTraveller = adults + childs + infants

    const handleTravelerMenuToggle = () => {
        if (active) {
            onClose()
        } else {
            onActivate()
        }
    }

    const handleTravelerIncrement = (type: 'adults' | 'childs' | 'infants') => {
        onTravelerChange(type, 1)
        const updatedData = {
            adults,
            childs,
            infants,
            [type]: (type === 'adults' ? adults : type === 'childs' ? childs : infants) + 1,
            flightClass
        }
        onChange?.(updatedData)
    }

    const handleTravelerDecrement = (type: 'adults' | 'childs' | 'infants') => {
        onTravelerChange(type, -1)
        const updatedData = {
            adults,
            childs,
            infants,
            [type]: (type === 'adults' ? adults : type === 'childs' ? childs : infants) - 1,
            flightClass
        }
        onChange?.(updatedData)
    }

    const handleClassChange = (newClass: string) => {
        onFlightClassChange(newClass)
        onChange?.({
            adults,
            childs,
            infants,
            flightClass: newClass
        })
    }

    const getCanIncrease = (type: 'adults' | 'childs' | 'infants') => {
        switch (type) {
            case 'adults':
                return canIncreaseAdults
            case 'childs':
                return canIncreaseChildren
            case 'infants':
                return canIncreaseInfants
            default:
                return false
        }
    }

    const getCanDecrease = (type: 'adults' | 'childs' | 'infants') => {
        switch (type) {
            case 'adults':
                return canDecreaseAdults
            case 'childs':
                return canDecreaseChildren
            case 'infants':
                return canDecreaseInfants
            default:
                return false
        }
    }

    return (
        <Box ref={travelersRef} sx={{ width: '100%' }}>
            <Button
                variant="text"
                onClick={handleTravelerMenuToggle}
                sx={{
                    textTransform: 'none',
                    backgroundColor: active ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    '&:hover': {
                        backgroundColor: active ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
                    },
                    width: '100%',
                    justifyContent: 'flex-start'
                }}
            >
                <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {totalTraveller} {t('traveller', { s: totalTraveller > 1 ? 's' : '' })}, {t(flightClass)}
                </Typography>
            </Button>
            <Popper
                anchorEl={anchorEl}
                open={popperOpen}
                placement="bottom-start"
                modifiers={[{ name: 'offset', options: { offset } }]}
                style={{ zIndex: SEARCH_MODIFIER_POPPER_Z_INDEX }}
            >
                <Paper
                    elevation={3}
                    onMouseDown={e => e.stopPropagation()}
                    sx={{
                        minWidth: 200,
                        py: 0.5,
                        mt: attachToSearchContainer ? 0 : 1.5
                    }}
                >
                    {TRAVELER_TYPES.map(({ key, label }) => (
                        <MenuItem disableRipple key={key}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <Typography variant="body2">{label}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleTravelerDecrement(key as 'adults' | 'childs' | 'infants')}
                                        disabled={!getCanDecrease(key as 'adults' | 'childs' | 'infants')}
                                        sx={{
                                            minWidth: 32,
                                            minHeight: 32,
                                            fontSize: '18px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        -
                                    </IconButton>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            minWidth: 24,
                                            textAlign: 'center',
                                            mx: 1
                                        }}
                                    >
                                        {key === 'adults' ? adults : key === 'childs' ? childs : infants}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleTravelerIncrement(key as 'adults' | 'childs' | 'infants')}
                                        disabled={!getCanIncrease(key as 'adults' | 'childs' | 'infants')}
                                        sx={{
                                            minWidth: 32,
                                            minHeight: 32,
                                            fontSize: '18px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        +
                                    </IconButton>
                                </Box>
                            </Box>
                        </MenuItem>
                    ))}
                    <MenuItem disableRipple>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                justifyContent: 'space-between',
                                gap: 1
                            }}
                        >
                            <Typography variant="body2">{t('class')}</Typography>
                            <Box>
                                <select
                                    value={flightClass}
                                    onChange={e => handleClassChange(e.target.value)}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: 4,
                                        border: '1px solid #ccc',
                                        fontSize: '14px',
                                        minWidth: 100
                                    }}
                                >
                                    <option value="Economy">{t('Economy')}</option>
                                    <option value="Business">{t('Business')}</option>
                                    <option value="First">{t('First')}</option>
                                    <option value="PREMIUM_ECONOMY">{t('PREMIUM_ECONOMY')}</option>
                                </select>
                            </Box>
                        </Box>
                    </MenuItem>
                </Paper>
            </Popper>
        </Box>
    )
}
